import { formatDate } from '@/Mixins/Formatting';

const _buildRule = (key, value, schema) => {
    if (value == null || (typeof value === 'string' && value.length === 0)) {
        return;
    }

    const operator = schema.dataFieldOperator || 'contains';
    const _key = schema.dataFieldOperand || key;
    const _value = 'dataFieldValue' in schema ? schema.dataFieldValue[value] : value;

    return {
        type: 'query-builder-rule',
        query: {
            rule: _key.replace('__', '.'),
            operator,
            operand: key,
            value: typeof _value === 'string' ? _value.trim() : _value,
        },
    };
};

const _buildArrayRule = (key, items, schema) => {
    if (!items || items.length === 0) {
        return;
    }

    const localGroup = {
        type: 'query-builder-group',
        query: {
            logicalOperator: 'any',
            children: [],
        },
    };

    const isJoinRelation = key.includes('##');

    // The relationships might be nested, e.g. 'orders__products__laboratory__name', so we'll split the relation accordingly.
    const relation = isJoinRelation
        ? key //
        : key.split('__').slice(0, -1).join('.');

    const field = isJoinRelation
        ? key.split('##').at(-1) //
        : key.split('__').at(-1);

    const operator = schema.dataFieldOperator || 'contains';

    items.forEach((item) => {
        localGroup.query.children.push({
            type: 'query-builder-rule',
            query: {
                operand: isJoinRelation
                    ? relation //
                    : relation
                        ? `${relation}.${field}`
                        : field,
                rule: isJoinRelation
                    ? relation //
                    : relation
                        ? `${relation}.${field}`
                        : field,
                operator,
                value: relation ? item[field] : item,
            },
        });
    });

    return localGroup;
};

const escapeFilterCharacters = (originalFilters, forceArrayResult = false) => {
    // forceArrayResult is used to maintain the same data structures as when this function is not in use
    // List of replacements being done on the filter values, for JSON.parse():
    //      \ to \\\\
    //      " to \"
    if (!originalFilters) return originalFilters;

    let filters;
    if (!forceArrayResult && Object.keys(originalFilters)) {
        filters = { ...originalFilters };
    } else {
        filters = originalFilters;
    }

    Object.keys(filters)?.forEach((filter) => {
        if (!filters[filter]) return;
        if (typeof filters[filter] === 'string' && (filters[filter].includes('"') || filters[filter].includes('\\'))) {
            filters[filter] = filters[filter]?.replaceAll("\\", "\\\\\\\\");
            filters[filter] = filters[filter]?.replaceAll('"', '\\"');
        } else if (Array.isArray(filters[filter]) && filters[filter].length) {
            filters[filter] = escapeFilterCharacters(filters[filter], true);
        }
    });

    return filters;
};

/// Middleware fields are filters that mutate other filters and aren't sent to the backend.
const applyMiddleware = (originalFilters, schema) => {
    const filters = { ...originalFilters };

    for (const middleware in schema) {
        const schemaField = schema[middleware];

        const hasMiddleware = 'dataFieldMiddleware' in schemaField;
        const isApplied = middleware in filters && filters[middleware];

        if (!hasMiddleware || !isApplied) {
            continue;
        }

        // The fields that we'll apply this middleware on, if they exist.
        const applyOn = schemaField.dataFieldApplyOn;

        for (const key in filters) {
            if (!applyOn.includes(key)) {
                continue;
            }

            let filter = filters[key];

            const value = 'dataFieldValue' in schemaField ? schemaField.dataFieldValue[filters[middleware]] : filters[middleware];

            // Apply the middleware
            switch (middleware) {
            case 'homologous_period': {
                const date = new Date(filter);
                date.setFullYear(date.getFullYear() - value);

                filter = formatDate(date);
                break;
            }

            default:
                console.error('Unknown middleware: ' + middleware);
            }

            // Set the filter to the new value after being processed by the middleware.
            filters[key] = filter;
        }

        // Remove the middleware as we don't want to send it to the backend.
        delete filters[middleware];
    }

    return filters;
};

/**
 * Adds a rule to the query object based on the group defined in the schema.
 * The operator used for the group is given by the caller.
 */
const _addRuleToGroup = (queryObject, rule, schema, operator) => {
    if (!('dataGroup' in schema)) {
        queryObject.children.push(rule);
        return;
    }

    // We have a custom group that we'll need to insert `rule` into.

    const group = queryObject.children.find((x) => {
        const isGroup = x.type === 'query-builder-group';
        const sameGroupName = x.name === schema.dataGroup;

        return isGroup && sameGroupName;
    });

    if (group) {
        group.query.children.push(rule);
        return;
    }

    queryObject.children.push({
        type: 'query-builder-group',
        name: schema.dataGroup,
        query: {
            logicalOperator: operator,
            children: [rule],
        },
    });
};

const getCombinationsAsString=(array)=> {
    let result = [];
    function permute(arr, prefix = []) {
        if (prefix.length === array.length) {
            result.push(prefix.join(" "));
            return;
        }
        for (let i = 0; i < arr.length; i++) {
            permute([...arr.slice(0, i), ...arr.slice(i + 1)], [...prefix, arr[i]]);
        }
    }
    permute(array);
    return result;
}

const buildQueryObjectFromModel = (filters, schema, { customSearch = {}, operator = 'all' } = {}) => {
    const queryObject = {
        logicalOperator: 'all',
        children: [],
    };
    let activeSpecialFilter=false
    filters = escapeFilterCharacters(filters);

    filters = applyMiddleware(filters, schema);

    if(window.location.href.indexOf('orders')!=-1 || window.location.href.indexOf('products')!=-1 ){
        activeSpecialFilter=true
    }else{
        activeSpecialFilter=false
    }
    for (const key in filters) {
        if (key in customSearch) {
            const value = _.toLower(filters[key]);
            if (!value) continue; // If the search term is not present in the filters, ignore it.

            let allValues = _.split(value,';')
            const urlParams = new URLSearchParams(window.location.search);
            const searchTerm = urlParams.get('exclude');
            const localGroup = {
                type: 'query-builder-group',
                query: {
                    logicalOperator: 'any',
                    children: [],
                },
            };

            if(_.size(allValues)>1){

               _.each(allValues,(val)=>{
                    const regex = new RegExp(`__${key}__`, 'g');
                    const customJSON = customSearch[key].replace(regex, val);
                    const children = JSON.parse(customJSON);
                    localGroup.query.children.push(children);
                })
                queryObject.children=[localGroup]
                excludeQuery(queryObject,searchTerm)

            }else{
                let allArrValues = _.split(_.trim(value),' ')

                if(_.size(allArrValues)>1 && _.size(allArrValues)<=2 &&  activeSpecialFilter){

                    _.each( getCombinationsAsString(allArrValues),(val)=>{
                        const regex = new RegExp(`__${key}__`, 'g');
                        const customJSON = customSearch[key].replace(regex, val);
                        const children = JSON.parse(customJSON);
                        localGroup.query.children.push(children);
                    })

                    if(value.indexOf('advance') !== -1){

                        let str= _.replace(value,'advance','adv')
                        let allArrValues2 = _.split(_.trim(str),' ')
                        _.each( getCombinationsAsString(allArrValues2),(val)=>{
                            const regex = new RegExp(`__${key}__`, 'g');
                            const customJSON = customSearch[key].replace(regex, val);
                            const children = JSON.parse(customJSON);
                            localGroup.query.children.push(children);
                        })
                    }

                    queryObject.children=[localGroup]
                    excludeQuery(queryObject,searchTerm)

                }else{

                    if(value.indexOf('advance') !== -1 &&  activeSpecialFilter){

                        const regex = new RegExp(`__${key}__`, 'g');
                        const customJSON = customSearch[key].replace(regex, value);
                        const children = JSON.parse(customJSON);
                        localGroup.query.children.push(children);
                        let str= _.replace(value,'advance','adv')
                        const regex_ = new RegExp(`__${key}__`, 'g');
                        const customJSON_ = customSearch[key].replace(regex_, str);
                        const children_ = JSON.parse(customJSON_);
                        localGroup.query.children.push(children_);
                        queryObject.children=[localGroup]

                    }else{

                        const regex = new RegExp(`__${key}__`, 'g');
                        const customJSON = customSearch[key].replace(regex, value);
                        const children = JSON.parse(customJSON);
                        queryObject.children.push(children);

                    }
                    excludeQuery(queryObject,searchTerm)
                }

            }
            // @Note: The custom search replaces the normal search for this term.
            continue;
        }

        const rule = Array.isArray(filters[key])
            ? _buildArrayRule(key, filters[key], schema[key]) //
            : _buildRule(key, filters[key], schema[key]);

        if (rule) {
            _addRuleToGroup(queryObject, rule, schema[key], operator);
        }
    }

    return queryObject;
};

const excludeQuery= (queryObject,searchTerm)=>{

    if(searchTerm){
        let allValuesExcluded = _.split(searchTerm,';')
        if(_.size(allValuesExcluded)>1){
            _.each(allValuesExcluded,(val)=>{
                let localGroup2 = {
                    type: 'query-builder-rule',
                    query: {
                          operand:'designation',
                          operator:'does_not_contain',
                          rule:'designation',
                          value: val
                        }
                    }
                    queryObject.children.push(localGroup2)
            })

        }else{
            const localGroup2 = {
                type: 'query-builder-rule',
                query: {
                      operand:'designation',
                      operator:'does_not_contain',
                      rule:'designation',
                      value:searchTerm
                    }
                }
            queryObject.children.push(localGroup2)
        }

    }
    //return queryObject;
}

const validateAdvancedFilters = (query) => {
    let errors = [];
    if (query?.children?.length > 0) {
        validatefilterChildren(query, errors);
    } else {
        errors.push('Deve adicionar pelo menos um filtro avançado para aplicar.');
    }

    return errors;
};

const validatefilterChildren = (filter, errors) => {
    // Recursive check for null values
    if (filter.children) {
        filter.children.forEach((child) => {
            // If child is a group of more rules and not a single rule, validate the rules in this group
            if (child.type == 'query-builder-group') {
                if (child.query.children.length > 0) {
                    validatefilterChildren(child.query, errors);
                } else {
                    errors.push('Os grupos de filtros avançados não podem estar vazios.');
                    return;
                }
                // Just a child (query rule), validate it
            } else {
                validatefilterChildren(child, errors);
            }
        });
    } else {
        if (filter.query.operator != 'não está vazio' && filter.query.operator != 'está vazio' && (filter.query.value == null || filter.query.value.toString().trim() == '')) {
            errors.push('Insira um valor em cada filtro avançado.');
        }
    }
};

export { buildQueryObjectFromModel, validateAdvancedFilters };
